class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.initialSearchState = this.querySelector("[data-search-initial]");
    this.isOpen = false;
    this.recentlyViewedFetched = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showInitialState();
      return;
    }

    this.hideInitialState();
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showInitialState();
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    fetch(
      `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.removeAttribute("show-initial");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
    if (this.initialSearchState) {
      this.initialSearchState.removeAttribute("style");
    }

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }

  showInitialState() {
    if (!this.initialSearchState) return;

    this.hideInitialState();
    this.setAttribute("show-initial", true);
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (this.initialSearchState) {
      this.initialSearchState.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }

    if (!this.recentlyViewedFetched) {
      this.fetchRecentlyViewedProducts();
    }

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  hideInitialState() {
    this.removeAttribute("show-initial");
  }

  fetchRecentlyViewedProducts() {
    const container = this.querySelector("[data-recently-viewed-container]");
    if (!container) {
      console.log("Recently viewed container not found");
      return;
    }

    // Get recently viewed products from localStorage
    let recentlyViewed = [];
    try {
      const stored = localStorage.getItem("recentlyViewedProducts");
      recentlyViewed = stored ? JSON.parse(stored) : [];
      console.log("Recently viewed from localStorage:", recentlyViewed);
    } catch (error) {
      console.error("Error parsing recently viewed products:", error);
      return;
    }

    if (!recentlyViewed || recentlyViewed.length === 0) {
      container.innerHTML = '<p style="text-align: center; color: rgba(var(--color-foreground), 0.6); font-size: 1.3rem;">No recently viewed products</p>';
      this.recentlyViewedFetched = true;
      return;
    }

    // Get last 4 products, most recent first
    const productsToShow = recentlyViewed.slice(-4).reverse();
    const searchString = productsToShow.map((product) => `id:${product.id}`).join(" OR ");
    
    console.log("Fetching products with query:", searchString);
    console.log("Search URL:", `${window.routes.search_url}?type=product&q=${encodeURIComponent(searchString)}`);

    // Fetch product details
    fetch(`${window.routes.search_url}?type=product&q=${encodeURIComponent(searchString)}`)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, "text/html");
        console.log("Search response received");
        
        // Find products using the correct selector
        let products = html.querySelectorAll(".product-grid .grid-item");
        
        console.log("Found products:", products.length);

        if (products.length === 0) {
          console.warn("No products found in search results");
          container.innerHTML = '<p style="text-align: center; color: rgba(var(--color-foreground), 0.6); font-size: 1.3rem;">No recently viewed products</p>';
          this.recentlyViewedFetched = true;
          return;
        }

        let cardsHTML = "";
        products.forEach((productElement, index) => {
          if (index >= 4) return;

          const productCard = productElement.querySelector("product-card");
          const link = productElement.querySelector("a.js-product-link");
          const image = productElement.querySelector(".card-media-image");
          const titleElement = productElement.querySelector(".card-heading");
          const vendorElement = productElement.querySelector(".card-eyebrow");
          const skuElement = productElement.querySelector(".card-sku");
          const price = productElement.querySelector(".price");
          const badgeElement = productElement.querySelector(".card-badge .badge");

          if (!link) {
            console.log("No link found in product element", index);
            return;
          }

          const productUrl = link.getAttribute("href");
          const imageUrl = image ? image.getAttribute("src") || image.getAttribute("srcset")?.split(" ")[0] : "";
          const imageAlt = image ? image.getAttribute("alt") : "";
          const productTitle = titleElement ? titleElement.textContent.trim() : "";
          const productVendor = vendorElement ? vendorElement.textContent.trim() : "";
          const productSku = skuElement ? skuElement.textContent.trim() : "";

          let priceHTML = "";
          if (price) {
            const regularPriceItem = price.querySelector(".price-item-regular");
            const salePriceItem = price.querySelector(".price-item-sale");

            if (regularPriceItem && salePriceItem) {
              // On sale - show both regular (strikethrough) and sale price
              priceHTML = `
                <div class="search-product-price">
                  <span class="search-product-price-regular">${regularPriceItem.textContent.trim()}</span>
                  <span class="search-product-price-sale">${salePriceItem.textContent.trim()}</span>
                </div>
              `;
            } else if (salePriceItem) {
              // Not on sale - just show the sale price item (which is actually the regular price)
              priceHTML = `
                <div class="search-product-price">
                  <span class="search-product-price-sale">${salePriceItem.textContent.trim()}</span>
                </div>
              `;
            } else {
              // Fallback - try to get any price text
              const priceText = price.textContent.trim();
              if (priceText) {
                priceHTML = `
                  <div class="search-product-price">
                    <span class="search-product-price-sale">${priceText}</span>
                  </div>
                `;
              }
            }
          }

          const badgeText = badgeElement ? badgeElement.textContent.trim() : "";

          cardsHTML += `
            <a href="${productUrl}" class="search-product-card">
              <div class="search-product-card-image-wrapper">
                ${badgeText ? `<span class="search-product-badge">${badgeText}</span>` : ""}
                ${imageUrl ? `<img src="${imageUrl}" alt="${imageAlt}" class="search-product-card-image" loading="lazy">` : ""}
              </div>
              <div class="search-product-card-info">
                ${productVendor ? `<p class="search-product-vendor">${productVendor}</p>` : ""}
                ${productTitle ? `<h4 class="search-product-title">${productTitle}</h4>` : ""}
                ${productSku ? `<p class="search-product-code">${productSku}</p>` : ""}
                ${priceHTML}
              </div>
            </a>
          `;
        });

        container.innerHTML = cardsHTML;
        this.recentlyViewedFetched = true;
      })
      .catch((error) => {
        console.error("Error fetching recently viewed products:", error);
        container.innerHTML = '<p style="text-align: center; color: rgba(var(--color-foreground), 0.6); font-size: 1.3rem;">Unable to load products</p>';
        this.recentlyViewedFetched = true;
      });
  }
}

customElements.define("predictive-search", PredictiveSearch);
